import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, MapPin, ChevronDown, RotateCcw, Train, Bus } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import "react-datepicker/dist/react-datepicker.css";
import api from '../services/regiojet-api';
import LoadingScreen from './LoadingScreen';
import PassengerSelect from './PassengerSelect';
import { getAllStations, findStationsByName } from '../utils/stations';
import { LANGUAGES } from '../utils/translations';

// Регистрируем русскую локаль
registerLocale('ru', ru);

// Форматирование даты для отображения
const formatDateForInput = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Форматирование даты для отправки на сервер
const formatDateForServer = (date) => {
  if (!(date instanceof Date)) {
    return new Date(date).toISOString().split('T')[0];
  }
  return date.toISOString().split('T')[0];
};

// Компонент выпадающего списка для поиска станций
const StationSearchDropdown = ({ 
  searchText, 
  onSearchChange, 
  placeholder, 
  label, 
  onSelect,
  selectedStation,
  locale
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const containerRef = useRef(null);

  // Обновление списка предложений при изменении текста поиска
  useEffect(() => {
    if (searchText && searchText.length > 1) {
      try {
        // Передаем локаль в функцию поиска
        const results = findStationsByName(searchText, locale);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (err) {
        console.error('Ошибка поиска станций:', err);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchText, locale]);

  // Скрытие выпадающего списка при клике вне него
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) &&
          suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Очистка поля ввода
  const handleClear = () => {
    onSearchChange('');
    onSelect(null);
  };

  // Выбор станции из списка
  const handleStationSelect = (station) => {
    onSelect(station);
    onSearchChange(station.name);
    setShowSuggestions(false);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="flex items-center">
        <MapPin className="text-gray-400 mr-3" size={20} />
        <div className="flex-grow">
          <div className="text-xs text-gray-500 font-medium">{label}</div>
          <input
            type="text"
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setShowSuggestions(suggestions.length > 0)}
            placeholder={placeholder}
            className="font-medium w-full outline-none bg-transparent"
            autoComplete="off"
            ref={inputRef}
          />
        </div>
        {searchText && (
          <button 
            type="button" 
            onClick={handleClear}
            className="p-1"
          >
            <span className="text-gray-400 text-lg">×</span>
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="absolute left-0 right-0 top-full bg-white shadow-lg z-[100] max-h-60 overflow-y-auto border border-gray-200 rounded-md mt-1"
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            width: '100%'
          }}
        >
          {suggestions.map(station => (
            <div
              key={station.id}
              className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center border-b border-gray-100"
              onClick={() => handleStationSelect(station)}
            >
              <MapPin className={selectedStation?.id === station.id ? "text-[#9a55f4] mr-2" : "text-gray-400 mr-2"} size={16} />
              <span className={selectedStation?.id === station.id ? "font-medium text-[#9a55f4]" : ""}>{station.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SearchForm = ({ onSearch, t, passengers, onPassengersChange, locale = LANGUAGES.RU }) => {
  // Состояния
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [stations, setStations] = useState([]);
  
  // Состояния для поиска
  const [fromSearchText, setFromSearchText] = useState('');
  const [toSearchText, setToSearchText] = useState('');
  const [selectedFromStation, setSelectedFromStation] = useState(null);
  const [selectedToStation, setSelectedToStation] = useState(null);
  
  // Новое состояние для типа транспорта
  const [transportType, setTransportType] = useState('all');
  
  // Форма
  const [formData, setFormData] = useState({
    fromLocationId: '',
    toLocationId: '',
    departureDate: formatDateForInput(new Date()),
    tariffs: ['REGULAR'],
    transportType: 'all'
  });

  // Загрузка локаций при монтировании
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getLocations();
        
        // Сортируем локации по имени
        const sortedLocations = response.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        setLocations(sortedLocations);
      } catch (err) {
        console.error('Error loading locations:', err);
        setError(t.errorLoadingLocations || 'Error loading locations');
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [t]);

  useEffect(() => {
    // Загружаем и сортируем все станции при монтировании компонента
    // используя текущую локаль пользователя
    const sortedStations = getAllStations(locale).sort((a, b) => 
      a.name.localeCompare(b.name, locale, { sensitivity: 'base' })
    );
    setStations(sortedStations);
  }, [locale]);

  // Обработчик выбора станции отправления
  const handleFromStationSelect = (station) => {
    if (!station) {
      setFormData(prev => ({ ...prev, fromLocationId: '' }));
      setSelectedFromStation(null);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      fromLocationId: station.id
    }));
    setSelectedFromStation(station);
  };

  // Обработчик выбора станции прибытия
  const handleToStationSelect = (station) => {
    if (!station) {
      setFormData(prev => ({ ...prev, toLocationId: '' }));
      setSelectedToStation(null);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      toLocationId: station.id
    }));
    setSelectedToStation(station);
  };

  // Обработчик поменять местами станции
  const handleSwapStations = () => {
    // Сохраняем текущие значения
    const tempFromText = fromSearchText;
    const tempFromStation = selectedFromStation;
    const tempFromId = formData.fromLocationId;
    
    // Устанавливаем новые значения
    setFromSearchText(toSearchText);
    setSelectedFromStation(selectedToStation);
    setToSearchText(tempFromText);
    setSelectedToStation(tempFromStation);
    
    // Обновляем formData
    setFormData(prev => ({
      ...prev,
      fromLocationId: prev.toLocationId,
      toLocationId: tempFromId
    }));
  };

  // Обработчик изменения даты
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({
      ...prev,
      departureDate: formatDateForInput(date)
    }));
  };

  // Обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (!formData.fromLocationId || !formData.toLocationId || !selectedDate) {
        throw new Error(t.pleaseSelectAllFields || 'Please select all required fields');
      }

      await onSearch({
        fromLocationId: Number(formData.fromLocationId),
        toLocationId: Number(formData.toLocationId),
        departureDate: formatDateForServer(selectedDate),
        passengers: Number(passengers),
        tariffs: formData.tariffs,
        transportType: transportType
      });
    } catch (err) {
      console.error('Search form error:', err);
      setError(err.message);
    }
  };

  // Обработчик изменения типа транспорта
  const handleTransportTypeChange = (type) => {
    setTransportType(type);
    setFormData(prev => ({
      ...prev,
      transportType: type
    }));
  };

  // Получение минимальной даты
  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Переводы для placeholder в зависимости от локали
  const getPlaceholders = () => {
    const placeholders = {
      [LANGUAGES.EN]: {
        from: 'Enter departure location',
        to: 'Enter arrival location'
      },
      [LANGUAGES.RU]: {
        from: 'Введите пункт отправления',
        to: 'Введите пункт прибытия'
      },
      [LANGUAGES.CS]: {
        from: 'Zadejte místo odjezdu',
        to: 'Zadejte místo příjezdu'
      },
      [LANGUAGES.PL]: {
        from: 'Wprowadź miejsce wyjazdu',
        to: 'Wprowadź miejsce przyjazdu'
      },
      [LANGUAGES.UK]: {
        from: 'Введіть пункт відправлення',
        to: 'Введіть пункт прибуття'
      }
    };

    return placeholders[locale] || placeholders[LANGUAGES.EN];
  };

  const placeholders = getPlaceholders();

  // Кастомный компонент для иконки календаря
  const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="relative w-full rounded-md cursor-pointer">
      <div 
        onClick={onClick}
        ref={ref}
        className="w-full flex items-center px-4 py-3 border border-gray-200 rounded-md shadow-sm hover:border-blue-300 transition-colors"
      >
        <Calendar className="text-gray-400 mr-3" size={20} />
        <div className="flex-grow">
          <div className="text-xs text-gray-500 font-medium">{t.departureDate || 'Дата отправления'}</div>
          <div className="font-medium">{value}</div>
        </div>
        <ChevronDown className="text-gray-400" size={18} />
      </div>
    </div>
  ));

  // Переводы для типов транспорта
  const getTransportTypeLabel = (type) => {
    const labels = {
      all: {
        [LANGUAGES.EN]: t.allTransport || 'All transport',
        [LANGUAGES.RU]: t.allTransport || 'Весь транспорт',
        [LANGUAGES.CS]: t.allTransport || 'Všechny dopravní prostředky',
        [LANGUAGES.PL]: t.allTransport || 'Wszystkie środki transportu',
        [LANGUAGES.UK]: t.allTransport || 'Весь транспорт'
      },
      train: {
        [LANGUAGES.EN]: t.trainOnly || 'Train only',
        [LANGUAGES.RU]: t.trainOnly || 'Только поезд',
        [LANGUAGES.CS]: t.trainOnly || 'Pouze vlak',
        [LANGUAGES.PL]: t.trainOnly || 'Tylko pociąg',
        [LANGUAGES.UK]: t.trainOnly || 'Тільки потяг'
      },
      bus: {
        [LANGUAGES.EN]: t.busOnly || 'Bus only',
        [LANGUAGES.RU]: t.busOnly || 'Только автобус',
        [LANGUAGES.CS]: t.busOnly || 'Pouze autobus',
        [LANGUAGES.PL]: t.busOnly || 'Tylko autobus',
        [LANGUAGES.UK]: t.busOnly || 'Тільки автобус'
      }
    };

    return labels[type][locale] || labels[type][LANGUAGES.EN];
  };

  return (
    <div className="w-full max-w-2xl mx-auto relative">
      {loading && (
        <LoadingScreen t={{
          loading: t.loadingLocations || 'Загрузка...',
          pleaseWait: t.pleaseWait || 'Пожалуйста, подождите'
        }} />
      )}
      
      <div className="relative h-64 mb-8 rounded-lg overflow-hidden">
        <img
          src="/travel-background.png"
          alt="Travel"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center text-white p-6">
          <h1 className="text-4xl font-bold mb-2">
            {t.bannerTitle || 'Путешествуйте с нами'}
          </h1>
          <p className="text-xl">
            {t.bannerSubtitle || 'Бронируйте билеты на поезд, автобус, самолет и паром'}
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
        {/* Заголовок */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          {t.searchRoutes || 'Поиск маршрутов'}
        </h2>

        {/* Выбор типа транспорта */}
        <div className="flex justify-center mb-4 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              transportType === 'all' ? 'bg-white shadow-sm text-[#9a55f4]' : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => handleTransportTypeChange('all')}
          >
            <span className="mr-2">{getTransportTypeLabel('all')}</span>
          </button>
          <button
            type="button"
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              transportType === 'train' ? 'bg-white shadow-sm text-[#9a55f4]' : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => handleTransportTypeChange('train')}
          >
            <Train className="mr-2" size={18} />
            <span>{getTransportTypeLabel('train')}</span>
          </button>
          <button
            type="button"
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              transportType === 'bus' ? 'bg-white shadow-sm text-[#9a55f4]' : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => handleTransportTypeChange('bus')}
          >
            <Bus className="mr-2" size={18} />
            <span>{getTransportTypeLabel('bus')}</span>
          </button>
        </div>

        {/* Блок поиска маршрута */}
        <div className="w-full relative rounded-lg border border-gray-200">
          {/* Поле "Откуда" */}
          <div className="w-full bg-white px-4 py-3 border-b relative">
            <StationSearchDropdown
              searchText={fromSearchText}
              onSearchChange={setFromSearchText}
              placeholder={placeholders.from}
              label={t.from || 'Откуда'}
              onSelect={handleFromStationSelect}
              selectedStation={selectedFromStation}
              locale={locale}
            />
          </div>
          
          {/* Поменять местами */}
          <div className="absolute left-1/2 top-1/2 z-10 transform -translate-x-1/2 -translate-y-1/2">
            <button 
              type="button" 
              onClick={handleSwapStations}
              className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="text-[#9a55f4]" size={18} />
            </button>
          </div>

          {/* Поле "Куда" */}
          <div className="w-full bg-white px-4 py-3 relative">
            <StationSearchDropdown
              searchText={toSearchText}
              onSearchChange={setToSearchText}
              placeholder={placeholders.to}
              label={t.to || 'Куда'}
              onSelect={handleToStationSelect}
              selectedStation={selectedToStation}
              locale={locale}
            />
          </div>
        </div>

        {/* Нижние параметры поиска */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Дата отправления */}
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="dd.MM.yyyy"
            minDate={getMinDate()}
            locale="ru"
            customInput={<CustomInput />}
            calendarClassName="border border-gray-200 rounded-lg shadow-lg"
            wrapperClassName="w-full"
            popperClassName="z-50"
            popperPlacement="bottom-start"
            placeholderText="ДД.ММ.ГГГГ"
          />

          {/* Пассажиры */}
          <div className="relative w-full rounded-md">
            <div className="w-full flex items-center px-4 py-3 border border-gray-200 rounded-md shadow-sm hover:border-blue-300 transition-colors cursor-pointer">
              <PassengerSelect 
                value={passengers}
                onChange={onPassengersChange}
                t={t}
              />
            </div>
          </div>
        </div>

        {/* Отображение ошибки */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Кнопка поиска */}
        <button
          type="submit"
          className="w-full bg-[#9a55f4] hover:bg-[#8645e0] text-white py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          disabled={loading}
        >
          <Search size={20} />
          <span>{t.search || 'Найти маршруты'}</span>
        </button>
      </form>
    </div>
  );
};

export default SearchForm;